/**
 * Simple custom pool physics - no external physics engine
 */

import {
  BALL_RADIUS,
  BALL_MASS,
  BALL_FRICTION,
  BALL_RESTITUTION,
  LETTER_BALL_RADIUS,
  LETTER_BALL_MASS,
  LETTER_BALL_FRICTION,
  LETTER_BALL_RESTITUTION,
  TABLE_LENGTH,
  TABLE_WIDTH,
  POCKET_RADIUS,
  POCKET_POSITIONS,
} from '../config/PhysicsConfig';

export interface Ball {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  mass: number;
  friction: number;
  restitution: number;
  letter?: string; // For letter balls
  isCueBall?: boolean;
}

interface Wall {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  restitution: number;
}

export type CollisionCallback = (ball1Id: string, ball2Id: string) => void;

export class SimplePoolPhysics {
  private balls: Ball[] = [];
  private walls: Wall[] = [];
  private cueBallId: string = 'cue-ball';
  private collisionCallback?: CollisionCallback;
  
  // Physics constants
  private readonly FRICTION_DECELERATION = 2.0; // m/s² (restored)
  private readonly WALL_RESTITUTION = 0.70;
  private readonly SUBSTEPS = 4; // Smaller timesteps to prevent tunneling
  
  constructor() {
    this.createWalls();
  }
  
  setCollisionCallback(callback: CollisionCallback): void {
    this.collisionCallback = callback;
  }
  
  private createWalls(): void {
    const halfWidth = TABLE_WIDTH / 2;
    const pocketRadius = POCKET_RADIUS;
    // Use actual side pocket position from config
    const sidePocketY = POCKET_POSITIONS.SIDE_LEFT.y;
    const gutterEndY = sidePocketY / 2; // Gutters extend to halfway to side pockets
    
    // Left wall (gutter end to side pocket) - NO wall in gutter zone
    this.walls.push({
      x1: -halfWidth, y1: gutterEndY,
      x2: -halfWidth, y2: sidePocketY - pocketRadius,
      restitution: this.WALL_RESTITUTION
    });
    
    // Left wall (side pocket to back)
    this.walls.push({
      x1: -halfWidth, y1: sidePocketY + pocketRadius,
      x2: -halfWidth, y2: TABLE_LENGTH,
      restitution: this.WALL_RESTITUTION
    });
    
    // Right wall (gutter end to side pocket) - NO wall in gutter zone
    this.walls.push({
      x1: halfWidth, y1: gutterEndY,
      x2: halfWidth, y2: sidePocketY - pocketRadius,
      restitution: this.WALL_RESTITUTION
    });
    
    // Right wall (side pocket to back)
    this.walls.push({
      x1: halfWidth, y1: sidePocketY + pocketRadius,
      x2: halfWidth, y2: TABLE_LENGTH,
      restitution: this.WALL_RESTITUTION
    });
    
    // Front wall (bumper at bottom to prevent balls falling off)
    this.walls.push({
      x1: -halfWidth, y1: 0,
      x2: halfWidth, y2: 0,
      restitution: this.WALL_RESTITUTION
    });
    
    // Back wall left segment
    this.walls.push({
      x1: -halfWidth, y1: TABLE_LENGTH,
      x2: -pocketRadius, y2: TABLE_LENGTH,
      restitution: this.WALL_RESTITUTION
    });
    
    // Back wall right segment
    this.walls.push({
      x1: pocketRadius, y1: TABLE_LENGTH,
      x2: halfWidth, y2: TABLE_LENGTH,
      restitution: this.WALL_RESTITUTION
    });
  }
  
  createCueBall(x: number, y: number): Ball {
    const cueBall: Ball = {
      id: this.cueBallId,
      x,
      y,
      vx: 0,
      vy: 0,
      radius: BALL_RADIUS,
      mass: BALL_MASS,
      friction: BALL_FRICTION,
      restitution: BALL_RESTITUTION,
      isCueBall: true,
    };
    
    this.balls.push(cueBall);
    return cueBall;
  }
  
  createLetterBall(id: string, x: number, y: number, letter: string): Ball {
    const ball: Ball = {
      id,
      x,
      y,
      vx: 0,
      vy: 0,
      radius: LETTER_BALL_RADIUS,
      mass: LETTER_BALL_MASS,
      friction: LETTER_BALL_FRICTION,
      restitution: LETTER_BALL_RESTITUTION,
      letter,
    };
    
    this.balls.push(ball);
    return ball;
  }
  
  launchCueBall(vx: number, vy: number): void {
    const cueBall = this.balls.find(b => b.id === this.cueBallId);
    if (cueBall) {
      cueBall.vx = vx;
      cueBall.vy = vy;
      const speed = Math.sqrt(vx * vx + vy * vy);
      console.log('🎱 Launched cue ball:', { vx, vy, speed: speed.toFixed(3) });
    }
  }
  
  update(deltaTime: number): void {
    // Use substeps for more accurate collision detection
    const dt = deltaTime / this.SUBSTEPS;
    
    for (let step = 0; step < this.SUBSTEPS; step++) {
      // Apply friction
      this.applyFriction(dt);
      
      // Update positions
      this.updatePositions(dt);
      
      // Detect and resolve collisions
      this.handleBallBallCollisions();
      this.handleBallWallCollisions();
    }
  }
  
  private applyFriction(dt: number): void {
    for (const ball of this.balls) {
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
      
      if (speed > 0.001) {
        // Friction force proportional to velocity
        const frictionForce = ball.friction * this.FRICTION_DECELERATION;
        const deceleration = Math.min(frictionForce * dt, speed);
        
        const scale = (speed - deceleration) / speed;
        ball.vx *= scale;
        ball.vy *= scale;
      } else {
        ball.vx = 0;
        ball.vy = 0;
      }
    }
  }
  
  private updatePositions(dt: number): void {
    for (const ball of this.balls) {
      ball.x += ball.vx * dt;
      ball.y += ball.vy * dt;
    }
  }
  
  private handleBallBallCollisions(): void {
    for (let i = 0; i < this.balls.length; i++) {
      for (let j = i + 1; j < this.balls.length; j++) {
        const b1 = this.balls[i];
        const b2 = this.balls[j];
        
        const dx = b2.x - b1.x;
        const dy = b2.y - b1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minDistance = b1.radius + b2.radius;
        
        if (distance < minDistance) {
          // Collision detected
          const nx = dx / distance;
          const ny = dy / distance;
          
          // Notify callback if one of the balls is the cue ball
          if (this.collisionCallback && (b1.isCueBall || b2.isCueBall)) {
            this.collisionCallback(b1.id, b2.id);
          }
          
          // Relative velocity
          const dvx = b1.vx - b2.vx;
          const dvy = b1.vy - b2.vy;
          const dvn = dvx * nx + dvy * ny;
          
          // Only resolve if approaching
          if (dvn > 0) {
            // Equal mass elastic collision
            const restitution = (b1.restitution + b2.restitution) / 2;
            const impulse = (1 + restitution) * dvn / 2;
            
            b1.vx -= impulse * nx;
            b1.vy -= impulse * ny;
            b2.vx += impulse * nx;
            b2.vy += impulse * ny;
            
            // Separate balls to prevent overlap
            const overlap = minDistance - distance;
            if (overlap > 0) {
              const separationX = (overlap / 2) * nx;
              const separationY = (overlap / 2) * ny;
              b1.x -= separationX;
              b1.y -= separationY;
              b2.x += separationX;
              b2.y += separationY;
            }
          }
        }
      }
    }
  }
  
  private handleBallWallCollisions(): void {
    for (const ball of this.balls) {
      for (const wall of this.walls) {
        // Calculate distance to wall (line segment)
        const dx = wall.x2 - wall.x1;
        const dy = wall.y2 - wall.y1;
        const length = Math.sqrt(dx * dx + dy * dy);
        
        if (length < 0.0001) continue;
        
        const nx = -dy / length; // Normal perpendicular to wall
        const ny = dx / length;
        
        // Distance from ball center to line
        const px = ball.x - wall.x1;
        const py = ball.y - wall.y1;
        const distToLine = px * nx + py * ny;
        
        // Project ball onto line to check if it's within segment
        const t = (px * dx + py * dy) / (length * length);
        
        if (t >= 0 && t <= 1 && Math.abs(distToLine) < ball.radius) {
          // Collision with wall
          const vn = ball.vx * nx + ball.vy * ny;
          
          // Reflect
          const impulse = (1 + wall.restitution) * vn;
          ball.vx -= impulse * nx;
          ball.vy -= impulse * ny;
          
          // Push ball away from wall
          const penetration = ball.radius - Math.abs(distToLine);
          if (penetration > 0) {
            const sign = distToLine < 0 ? -1 : 1;
            ball.x += sign * penetration * nx;
            ball.y += sign * penetration * ny;
          }
        }
      }
    }
  }
  
  getBalls(): Ball[] {
    return this.balls;
  }
  
  getCueBall(): Ball | undefined {
    return this.balls.find(b => b.id === this.cueBallId);
  }
  
  removeBall(id: string): void {
    this.balls = this.balls.filter(b => b.id !== id);
  }
  
  clearAllBalls(): void {
    this.balls = [];
    this.cueBallId = '';
  }
  
  areAllBallsStopped(): boolean {
    const threshold = 0.01; // m/s
    return this.balls.every(ball => {
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
      return speed < threshold;
    });
  }
}
